import { AdjustIcon } from "@components/Icons";
import queryKeys from "@configs/react-query/query-key";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useEffect, useRef, useState } from "react";
import AdvancedSearch from "../Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { getEffectiveQuickSearchValue } from "@utils/searchHelpers";
import TableWithPagination from "../Tables/TableWithPagination";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import useEscapeKey from "@hooks/useEscapeKey";

const AddVerificationFileModal = ({ isOpen, onClose, onAdd, initialSelectedFile, "data-testid": dataTestId = "add-verification-file-modal" }) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [verificationFiles, setVerificationFiles] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.ADD_VERIFICATION_REPORT_LIST, DEFAULT_ITEMS_PER_PAGE);

  const advancedSearchFields = [
    {
      name: "projectId",
      label: t("project.project-id"),
      type: "text",
      placeholder: t("project.project-id"),
    },
    {
      name: "verificationVersion",
      label: t("common.version"),
      type: "text",
      placeholder: t("common.version"),
    },
    {
      name: "realFileName",
      label: t("project.verification.verification-file-name"),
      type: "text",
      placeholder: t("project.verification.verification-file-name"),
    },
  ];

  // API call to get binary verifications
  const getSourceCodeVerificationsWithClarity = useMutationData(
    queryKeys.getSourceCodeVerificationsWithClarity,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setVerificationFiles(response.object || []);
          setTotalFiles(response.objectCount || 0);
          setTotalPages(Math.ceil((response.objectCount || 0) / 10));
        }
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        console.error("Error fetching binary verifications:", error);
      },
    },
  );

  const { showAdvancedSearch, setShowAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    onPanelClose: () => {
      setSearchFilters({});
    },
  });

  const fetchFiles = (page, searchValue, filters) => {
    setLoading(true);
    const startRow = (page - 1) * 10;
    const { projectName: advancedProjectName, ...restFilters } = filters;
    const quickProjectName = getEffectiveQuickSearchValue(searchValue, {
      isAdvancedPanelOpen: showAdvancedSearch,
    });
    const projectName = showAdvancedSearch ? advancedProjectName : quickProjectName;
    getSourceCodeVerificationsWithClarity.mutate({
      ...restFilters,
      ...(projectName ? { projectName } : {}),
      startRow,
      pageSize: itemsPerPage,
    });
  };

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setSearchFilters({});
      setShowAdvancedSearch(false);
      setCurrentPage(1);
      setSelectedFile(initialSelectedFile || null);
      fetchFiles(1, "", {});
      isInitialMount.current = true;
    }
    // eslint-disable-next-line
  }, [isOpen, initialSelectedFile]);

  useEffect(() => {
    if (!isOpen) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchFiles(currentPage, search, searchFilters);
    // eslint-disable-next-line
  }, [currentPage, itemsPerPage, showAdvancedSearch, searchFilters]);

  // Debounce search
  useEffect(() => {
    if (!isOpen) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setIsSearching(true);
    debounceTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchFiles(1, search, searchFilters);
      setIsSearching(false);
    }, 600);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line
  }, [search]);

  useEscapeKey(onClose, isOpen);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleAdvancedSearch = (fields) => {
    setSearchFilters(fields);
    setCurrentPage(1);
    fetchFiles(1, search, fields);
  };

  const handleAdd = () => {
    if (selectedFile) {
      onAdd(selectedFile);
    }
  };
    const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  // Status styles (text color)
  const statusStyles = {
    ON_ANALYSIS: colors.textStatusAnalysis,
    ANALYSIS_CHECK: colors.textStatusAnalysis,
    REQUEST: colors.textStatusRequest,
    ON_VERIFICATION: colors.textStatusVerification,
    ON_REVIEW: colors.textStatusReview,
    STANDBY_FOLLOWUP: colors.textStatusFollowup,
    FOLLOWUP_CHECK: colors.textStatusFollowup,
    DONE: colors.textStatusSuccess,
    REVERIFICATION: colors.textStatusReverification,
    ERROR: colors.textStatusAnalysis,
  };

  // Status color dots (background color)
  const statusColorDot = {
    ON_ANALYSIS: colors.bgStatusAnalysis,
    ANALYSIS_CHECK: colors.bgStatusAnalysis,
    REQUEST: colors.bgStatusRequest,
    ON_VERIFICATION: colors.bgStatusVerification,
    ON_REVIEW: colors.bgStatusReview,
    STANDBY_FOLLOWUP: colors.bgStatusFollowup,
    FOLLOWUP_CHECK: colors.bgStatusFollowup,
    DONE: colors.bgStatusSuccess,
    REVERIFICATION: colors.bgStatusReverification,
    ERROR: colors.bgStatusAnalysis,
  };

  // Status text translations
  const statusText = {
    ON_ANALYSIS: t("status.analyzing"),
    ANALYSIS_CHECK: t("status.analysisCheck"),
    REQUEST: t("status.request"),
    ON_VERIFICATION: t("status.onVerification"),
    ON_REVIEW: t("status.onReview"),
    STANDBY_FOLLOWUP: t("status.standbyFollowup"),
    FOLLOWUP_CHECK: t("status.followupCheck"),
    DONE: t("status.done"),
    REVERIFICATION: t("status.reverification"),
    ERROR: t("status.error"),
  };

  // Render verification status with styling similar to ProjectList
  const renderVerificationStatus = (value, row) => {
    const statusCode = row.verificationStatusCd;
    const statusName = row.verificationStatusCdName;
    const vt = row.verificationType;
    let toolName = t("common.fossid");
    if (vt === "clarity") {
      toolName = t("common.clarity");
    } else if (vt === "fossid") {
      toolName = t("common.fossid");
    } else if (vt === "clarity_air" || vt === "clarityair") {
      toolName = t("common.clarityair");
    } else if (row.clarityFlag === "Y") {
      toolName = t("common.clarity");
    }

    if (!statusCode) {
      return <span className={colors.textMuted}>{toolName}</span>;
    }

    // For ON_VERIFICATION status, use the specific status name if available
    let displayText = statusText[statusCode];
    if (statusCode === "ON_VERIFICATION" && statusName) {
      // Map the status name to translation keys
      const statusNameMap = {
        "Usage Type Review": t("status.usageTypeReview"),
        "Usage Type Check": t("status.usageTypeCheck"),
        "Usage Type Input": t("status.usageTypeInput"),
      };
      
      displayText = statusNameMap[statusName] || statusName;
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 text-sm text-[13px] leading-none font-semibold ${statusStyles[statusCode] || `${colors.bgStatusTagNeutral} ${colors.textPrimary}`}`}
      >
        <span className={`${colors.textPrimary} mr-2`}>{toolName}</span>
        <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[statusCode]}`}></div>
        {displayText}
      </span>
    );
  };

  // Table columns and config
  const columns = [
    "projectName",
    "projectId",
    "verificationVersion",
    "realFileName",
    "verificationStatusCdName",
    "createrName",
    "createdAt",
  ];

  const columnConfig = {
    verificationVersion: {
      title: t("common.version"),
      style: { width: "12%", textAlign: "center" },
    },
    realFileName: {
      title: t("project.verification.verification-file-name"),
      style: { width: "18%",textAlign: "left" },
    },
    verificationStatusCdName: {
      title: t("project.verification.verif-Status"),
      customRender: (value, row) => renderVerificationStatus(value, row),
      style: { 
        textAlign: "left",
        width: "20%",
      },
    },
    projectId: {
      title: t("project.project-id"),
      style: { width: "14%", textAlign: "left" },
    },
    projectName: {
      title: t("project.project-name"),
      style: { width: "16%", textAlign: "left" },
    },
    createrName: {
      title: t("common.creator"),
      style: { width: "10%", textAlign: "left" },
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      title: t("common.create-at"),
      customRender: (value) => formatDateTime(value),
      style: { width: "15%", textAlign: "left" },
    },
  };

  // Radio selection logic for TableWithPagination
  const handleRadioTableChange = (selected) => {
    if (selected && selected.length > 0) {
      const file = verificationFiles.find((f) => f.verificationSeq === selected[0]);
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
      <div
        className={`${colors.bgModal} w-[1000px] min-w-[1000px] max-h-[95vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl`}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`px-6 py-4`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>{t("project.verification.add-verification-project-version")}</h2>
          </div>
        </div>

        {/* Search Section */}
        <div className="space-y-4 px-6 pb-4" data-testid={`${dataTestId}-content`}>
          <div className="flex items-center gap-4 max-w-xl">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon
                className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
              />
              <input
                type="text"
                placeholder={t("project.verification.search-by-project-name")}
                value={search}
                onChange={handleSearch}
                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                data-testid={fieldInputTestId("search")}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.textActive}`}></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("common.advanced-search")}</span>
            </button>
          </div>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="relative mx-6 mb-4">
            <AdvancedSearch
              title={t("project.advancedSearch")}
              searchFields={advancedSearchFields}
              searchHandler={handleAdvancedSearch}
              withoutSearch={true}
              gridCols="md:grid-cols-2 lg:grid-cols-3"
              padding="p-4"
            />
          </div>
        )}

        {/* TableWithPagination */}
        <div className="mb-4 px-6" style={{ overflow: "hidden" }}>
          <TableWithPagination
            columns={columns}
            columnConfig={columnConfig}
            data={verificationFiles}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={loading}
            rowKey="verificationSeq"
            totalPages={totalPages}
            totalData={totalFiles}
            maxRowsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChangeWithReset}
            hasRadioButton={true}
            selectedRows={selectedFile ? [selectedFile.verificationSeq] : []}
            onRowClick={(row) => setSelectedFile(row)}
            onCheckboxChange={handleRadioTableChange}
            showPagination={true}
            scrollY={showAdvancedSearch ? "350px" : "550px"}
            paddingYHeader="py-2"
            stickyHeader={true}
            showItemsPerPageSelector={true}
          />
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={`${dataTestId}-confirm-button`}
                onClick={handleAdd}
                disabled={!selectedFile}
              >
                {t("common.add-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVerificationFileModal;
